import type { OrderDetail } from '@skupreme/api-contracts/core';
import { LABEL_GENERATION } from '@features/orders';

type OrderErrorEntry = NonNullable<OrderDetail['order_error']>[number];

/* The reason string the pipeline writes for an address it could not use. */
const INVALID_ADDRESS = 'Invalid Address';

/* The status whose screen hides its issues, so none is worth waiting on. */
const SHIPPED = 'Shipped';

/**
 * How often an order still carrying an unresolved issue re-reads itself.
 *
 * The pipeline resolves an order_error in Mongo alone: the only realtime
 * channel this screen has is the Firestore fulfillment-request listener, and
 * no resolution writer touches it. Re-reading is the one way the screen can
 * learn, so an order showing an issue asks again until the issue is gone.
 */
export const ORDER_ISSUE_REVALIDATE_INTERVAL = 15_000;

/**
 * The wire type promises an array, but a legacy order can still carry a bare
 * order_error object instead of one.
 */
type OrderErrors = OrderErrorEntry[] | OrderErrorEntry | undefined;

type RevalidatedOrder = {
  order_error?: OrderErrors;
  summary?: { status?: string };
};

/** Whether the order still shows an issue a resolution could clear. */
export function hasUnresolvedOrderIssue(
  order: RevalidatedOrder | undefined,
): boolean {
  if (!order || order.summary?.status === SHIPPED) {
    return false;
  }

  const errors = order.order_error;
  const list = !errors ? [] : Array.isArray(errors) ? errors : [errors];

  /* A reasonless entry renders nothing, so it is not what the screen waits on. */
  return list.some(error => !error.resolved && !!error.reason);
}

/**
 * The interval an order detail query re-reads itself at, or `false` to leave
 * it alone. Polling stops as soon as the last issue resolves, so only orders
 * actually showing a banner ask again.
 */
export function getOrderRevalidateInterval(
  order: RevalidatedOrder | undefined,
): number | false {
  return hasUnresolvedOrderIssue(order)
    ? ORDER_ISSUE_REVALIDATE_INTERVAL
    : false;
}

/**
 * Whether a list still shows an issue a resolution could clear. The rows share
 * one request, so one of them waiting is enough to keep the list waiting.
 *
 * A list row carries no status of its own worth reading -- the queue it was
 * asked for decides what it shows -- so the shipped queue is turned away here
 * rather than row by row: it hides every issue it holds.
 */
export function hasUnresolvedListIssue(
  orders: RevalidatedOrder[] | undefined,
  status?: string,
): boolean {
  if (status === SHIPPED) {
    return false;
  }

  return (orders || []).some(order => hasUnresolvedOrderIssue(order));
}

/**
 * The interval an order list query re-reads itself at, or `false` to leave it
 * alone. Same policy as a single order, over the rows of one page: a list with
 * nothing on show never asks, however long it is left open.
 */
export function getOrderListRevalidateInterval(
  orders: RevalidatedOrder[] | undefined,
  status?: string,
): number | false {
  return hasUnresolvedListIssue(orders, status)
    ? ORDER_ISSUE_REVALIDATE_INTERVAL
    : false;
}

/**
 * The unresolved order_error entries the summary card's chip row should show.
 * A missing-dimensions label error is deliberately excluded: it has its own
 * banner above the cards (LabelBlockedBanner), so showing it again here would
 * duplicate the warning rather than add information.
 */
export function getSummaryCardIssues(
  errors: OrderErrorEntry[] | undefined,
): OrderErrorEntry[] {
  return (errors || []).filter(
    error =>
      !error.resolved &&
      error.reason !== INVALID_ADDRESS &&
      error.process !== LABEL_GENERATION,
  );
}
